<template>
<div class="home-container">
  <template v-if="isCustomer">
    <div class="customer-dashboard-heading">
      <div>
        <h1>{{ $t('工作台') }}</h1>
        <p>{{ $t(`你好，${nickname || $t('客户')}，这里是你的物流业务概览。`) }}</p>
      </div>
      <el-button icon="el-icon-refresh" :loading="dashboardLoading" @click="loadCustomerDashboard">{{ $t('刷新数据') }}</el-button>
    </div>

    <el-row :gutter="16" class="dashboard-row">
      <el-col :xs="24" :sm="8">
        <section class="dashboard-panel todo-panel">
          <div class="panel-heading"><h2>{{ $t('待办事项') }}</h2><span>{{ $t('需要关注的业务') }}</span></div>
          <button type="button" class="todo-item" @click="goCustomerRoute('/transport/tr-orders', { billingStatus: 'OUTSTANDING' })"><span>{{ $t('待出账订单') }}</span><strong>{{ customerStats.pendingBillingOrders }}</strong></button>
          <button type="button" class="todo-item" @click="goCustomerRoute('/transport/fin-deposits')"><span>{{ $t('待缴费押金') }}</span><strong>{{ formatMoney(customerAccount.depositShortfall) }} USD</strong></button>
          <button type="button" class="todo-item" @click="goCustomerRoute('/transport/tr-claims', { status: 'OPEN' })"><span>{{ $t('待处理索赔') }}</span><strong>{{ customerStats.pendingClaims }}</strong></button>
          <button type="button" class="todo-item" @click="goCustomerRoute('/transport/tr-disputes', { closed: 'false' })"><span>{{ $t('待处理争议') }}</span><strong>{{ customerStats.pendingDisputes }}</strong></button>
        </section>
      </el-col>
      <el-col :xs="24" :sm="8">
        <section class="dashboard-panel quick-panel">
          <div class="panel-heading"><h2>{{ $t('快捷操作') }}</h2><span>{{ $t('常用功能') }}</span></div>
          <div class="quick-action-grid">
            <button type="button" class="quick-action" @click="goCustomerRoute('/transport/tr-create')"><i class="el-icon-edit-outline" /><span>{{ $t('创建订单') }}</span></button>
            <button type="button" class="quick-action" @click="goCustomerRoute('/transport/tr-orders')"><i class="el-icon-tickets" /><span>{{ $t('订单管理') }}</span></button>
            <button type="button" class="quick-action" @click="goCustomerRoute('/transport/fin-accounts')"><i class="el-icon-wallet" /><span>{{ $t('资金账户') }}</span></button>
            <button type="button" class="quick-action" @click="goCustomerRoute('/transport/tr-claims')"><i class="el-icon-document-add" /><span>{{ $t('提交索赔') }}</span></button>
          </div>
        </section>
      </el-col>
      <el-col :xs="24" :sm="8">
        <section class="dashboard-panel notice-panel">
          <div class="panel-heading"><h2>{{ $t('系统公告') }}</h2><span>{{ $t('最近公告') }}</span></div>
          <div v-if="dashboardNotices.length" class="dashboard-notice-list" role="list" :aria-label="$t('系统公告记录')">
            <button v-for="notice in dashboardNotices" :key="notice.id" type="button" class="notice-row" role="listitem" @click="openCustomerNotice(notice)">
              <span class="notice-row-title" data-i18n-ignore>{{ notice.title }}</span>
              <time>{{ formatDate(notice.createTime) }}</time>
            </button>
          </div>
          <div v-else class="notice-empty">
            <i class="el-icon-message" />
            <span>{{ $t('暂无系统公告') }}</span>
          </div>
        </section>
      </el-col>
    </el-row>

    <section v-loading="customerAccountLoading" class="dashboard-panel balance-panel">
      <div class="panel-heading"><h2>{{ $t('账户余额') }}</h2><button type="button" @click="goCustomerRoute('/transport/fin-accounts')">{{ $t('查看资金账户') }}</button></div>
      <div class="balance-content">
        <div class="balance-main"><span>{{ $t('可用余额') }}</span><strong>{{ formatMoney(customerAccount.availableBalance) }}</strong><em>USD</em></div>
        <div class="balance-item"><span>{{ $t('当前余额') }}</span><strong>{{ formatMoney(customerAccount.balance) }} USD</strong></div>
        <div class="balance-item"><span>{{ $t('冻结金额') }}</span><strong>{{ formatMoney(customerAccount.pendingAmount) }} USD</strong></div>
        <div class="balance-item"><span>{{ $t('押金') }}</span><strong>{{ formatMoney(customerAccount.deposit) }} USD</strong></div>
      </div>
    </section>

    <el-row :gutter="16" class="dashboard-row">
      <el-col :xs="24" :md="16">
        <section class="dashboard-panel statistics-panel">
          <div class="panel-heading"><h2>{{ $t('订单统计') }}</h2><span>{{ $t('近30天') }}</span><button type="button" @click="goCustomerRoute('/transport/tr-orders')">{{ $t('查看全部') }}</button></div>
          <div class="statistics-grid">
            <button type="button" class="stat-item" @click="goCustomerRoute('/transport/tr-orders')"><span>{{ $t('订单总数') }}</span><strong>{{ customerStats.totalOrders }}</strong></button>
            <button type="button" class="stat-item" @click="goCustomerRoute('/transport/tr-orders', { status: 'SUCCESS' })"><span>{{ $t('预报成功') }}</span><strong>{{ customerStats.successOrders }}</strong></button>
            <div class="stat-item"><span>{{ $t('件数') }}</span><strong>{{ customerStats.pieceCount }}</strong></div>
            <div class="stat-item"><span>{{ $t('预报重量') }}</span><strong>{{ customerStats.forecastWeightKg }} KG</strong></div>
            <div class="stat-item"><span>{{ $t('已出账金额') }}</span><strong>{{ formatMoney(customerStats.billedAmount) }} USD</strong></div>
            <div class="stat-item"><span>{{ $t('预扣费金额') }}</span><strong>{{ formatMoney(customerStats.deductedAmount) }} USD</strong></div>
          </div>
          <div class="trend-toolbar">
            <el-radio-group v-model="orderTrendMetric" size="mini" @change="renderOrderChart">
              <el-radio label="count">{{ $t('票数') }}</el-radio>
              <el-radio label="weight">{{ $t('重量') }}</el-radio>
              <el-radio label="amount">{{ $t('预扣费') }}</el-radio>
            </el-radio-group>
          </div>
          <div ref="orderTrendChart" class="order-trend-chart"></div>
          <div v-if="recentOrders.length" class="recent-orders">
            <div class="subheading">{{ $t('最近订单') }}</div>
            <button v-for="order in recentOrders" :key="order.id" type="button" class="recent-order-row" @click="goCustomerRoute('/transport/tr-orders', { referenceNo: order.referenceNo })"><span>{{ order.referenceNo }}</span><span>{{ order.productName || '-' }}</span><span>{{ orderStatusText(order.status) }}</span><strong>{{ order.status === 'SUCCESS' ? `${formatMoney(order.customerAmount)} USD` : '-' }}</strong></button>
          </div>
        </section>
      </el-col>
      <el-col :xs="24" :md="8">
        <section class="dashboard-panel message-panel">
          <div class="panel-heading"><h2>{{ $t('消息通知') }}</h2><span v-if="customerStats.unreadMessages">{{ $t(`${customerStats.unreadMessages} 条未读`) }}</span></div>
          <div v-if="dashboardMessages.length" class="message-list">
          <button v-for="message in dashboardMessages" :key="message.id" type="button" class="message-row" @click="openCustomerMessage(message)"><i :class="message.unread ? 'el-icon-message-solid' : 'el-icon-message'" /><span data-i18n-ignore>{{ message.title }}</span><time>{{ formatDate(message.createTime) }}</time></button>
          </div>
          <el-empty v-else :description="$t('暂无消息通知')" :image-size="54" />
        </section>
      </el-col>
    </el-row>

    <el-dialog :title="$t('系统公告')" :visible.sync="customerNoticeDialog" width="900px" class="customer-notice-dialog" @closed="balanceReminderNoticesReady = true">
      <div class="customer-notice-dialog-body">
        <div v-if="selectedCustomerNotice" class="customer-notice-dialog-content">
          <div class="customer-notice-dialog-kicker"><i class="el-icon-bell" />{{ $t('系统公告') }}</div>
          <h3 data-i18n-ignore>{{ selectedCustomerNotice.title }}</h3>
          <div class="customer-notice-dialog-time">{{ formatDate(selectedCustomerNotice.createTime) }}</div>
          <!-- Recreate the document on every open instead of reusing a hidden frame. -->
          <iframe v-if="customerNoticeDialog" :key="selectedCustomerNotice.id" :srcdoc="customerNoticeDocument" sandbox="" :title="$t('公告内容')" />
        </div>
        <el-empty v-else :description="$t('暂无系统公告')" :image-size="60" />
      </div>
      <span slot="footer" class="customer-notice-dialog-footer"><el-button type="text" @click="dismissCustomerNotice">{{ $t('不再显示') }}</el-button><el-button type="primary" @click="customerNoticeDialog = false">{{ $t('知道了') }}</el-button></span>
    </el-dialog>
    <low-balance-notice :account="customerAccount" :ready="balanceReminderAccountReady && balanceReminderNoticesReady && balanceReminderAccountSession === balanceReminderSessionKey" :blocked="customerNoticeDialog || $route.path !== '/home'" :session-key="balanceReminderSessionKey" />
  </template>
  <template v-else>
    <div v-loading="adminOverviewLoading" class="operations-dashboard">
      <div class="operations-heading">
        <div><h1>{{ $t('运行总览') }}</h1><p>{{ $t('订单、资金和客户异常集中监控 · 最近更新 ') }}{{ formatDate(adminOverview.generatedAt) }}</p></div>
        <el-button icon="el-icon-refresh" :loading="adminOverviewLoading" @click="loadAdminOverview">{{ $t('刷新数据') }}</el-button>
      </div>

      <section class="order-intake-control" :class="{ paused: adminOverview.orderIntake.paused }">
        <div class="control-icon"><i :class="adminOverview.orderIntake.paused ? 'el-icon-video-pause' : 'el-icon-circle-check'" /></div>
        <div class="control-copy">
          <div class="control-title"><span>{{ $t('客户新订单入口') }}</span><el-tag size="small" :type="adminOverview.orderIntake.paused ? 'danger' : 'success'">{{ adminOverview.orderIntake.paused ? $t('已紧急暂停') : $t('运行正常') }}</el-tag></div>
          <p v-if="adminOverview.orderIntake.paused">{{ $t('暂停原因：') }}{{ adminOverview.orderIntake.reason || $t('系统维护') }}<span v-if="adminOverview.orderIntake.pausedByName"> · {{ adminOverview.orderIntake.pausedByName }}</span><span v-if="adminOverview.orderIntake.pausedAt"> · {{ formatDate(adminOverview.orderIntake.pausedAt) }}</span></p>
          <p v-else>{{ $t('客户可以正常试算和创建订单。暂停后仍可登录、查看历史订单和资金账户。') }}</p>
        </div>
        <el-button v-if="adminOverview.orderIntake.paused" type="success" :loading="adminControlSaving" @click="resumeOrderIntake">{{ $t('恢复客户下单') }}</el-button>
        <el-button v-else type="danger" plain :loading="adminControlSaving" @click="pauseOrderIntake">{{ $t('紧急暂停下单') }}</el-button>
      </section>

      <div class="operations-metrics">
        <button v-for="item in adminMetricCards" :key="item.key" type="button" class="operation-metric" @click="item.path && gotoRoute(item.path, item.query)">
          <span>{{ item.label }}</span><strong>{{ item.money ? `$${formatMoney(item.value)}` : Number(item.value || 0).toLocaleString() }}</strong><small>{{ item.tip }}</small>
        </button>
      </div>

      <div class="operations-content-grid">
        <section class="operations-panel issue-panel">
          <div class="operations-panel-heading"><div><h2>{{ $t('需要管理员处理的问题') }}</h2><p>{{ $t('按影响程度排列，点击可前往对应功能') }}</p></div><span>{{ adminOverview.issues.length }}{{ $t(' 项') }}</span></div>
          <div v-if="adminOverview.issues.length" class="operation-issue-list">
            <button v-for="issue in adminOverview.issues" :key="issue.code" type="button" class="operation-issue" :disabled="!issue.path" @click="openOperationIssue(issue)">
              <span class="issue-indicator" :class="`is-${issue.severity}`" /><span class="issue-copy"><strong>{{ issue.title }}</strong><small>{{ issue.description }}</small></span><el-tag size="small" :type="issue.severity === 'danger' ? 'danger' : issue.severity === 'warning' ? 'warning' : 'info'">{{ issue.count }}{{ $t(' 项') }}</el-tag><i v-if="issue.path" class="el-icon-arrow-right" />
            </button>
          </div>
          <div v-else class="operation-healthy"><i class="el-icon-circle-check" /><strong>{{ $t('当前没有待处理异常') }}</strong><span>{{ $t('订单、资金和接口运行正常') }}</span></div>
        </section>

        <section class="operations-panel quick-operations-panel">
          <div class="operations-panel-heading"><div><h2>{{ $t('紧急处置') }}</h2><p>{{ $t('常用管理入口') }}</p></div></div>
          <div class="operation-quick-grid">
            <button type="button" @click="gotoRoute('/transport/tr-orders', { status: 'REVIEW_REQUIRED' })"><i class="el-icon-warning-outline" /><span>{{ $t('异常订单') }}</span></button>
            <button type="button" @click="gotoRoute('/transport/tr-products')"><i class="el-icon-box" /><span>{{ $t('产品启停') }}</span></button>
            <button type="button" @click="gotoRoute('/system/user')"><i class="el-icon-user" /><span>{{ $t('客户账号') }}</span></button>
            <button type="button" @click="gotoRoute('/transport/fin-ledger')"><i class="el-icon-coin" /><span>{{ $t('资金核对') }}</span></button>
          </div>
        </section>
      </div>

      <section class="operations-panel exception-orders-panel">
        <div class="operations-panel-heading"><div><h2>{{ $t('最近异常订单') }}</h2><p>{{ $t('展示待核对和预报失败的最新订单') }}</p></div><button type="button" @click="gotoRoute('/transport/tr-orders')">{{ $t('查看全部') }}</button></div>
        <div v-if="adminOverview.exceptionOrders.length" class="exception-order-list">
          <button v-for="order in adminOverview.exceptionOrders" :key="order.id" type="button" class="exception-order" @click="gotoRoute('/transport/tr-orders', { status: order.status, referenceNo: order.referenceNo })">
            <span class="exception-main"><strong>{{ order.referenceNo }}</strong><small>{{ order.customerName }} · {{ order.productName || '-' }}</small></span>
            <span class="exception-reason">{{ order.failureReason || (order.status === 'REVIEW_REQUIRED' ? $t('等待管理员核对上游结果') : $t('上游预报失败')) }}</span>
            <el-tag size="small" :type="order.status === 'API_FAILED' ? 'danger' : 'warning'">{{ orderStatusText(order.status) }}</el-tag>
            <time>{{ formatDate(order.createdAt) }}</time><i class="el-icon-arrow-right" />
          </button>
        </div>
        <el-empty v-else :description="$t('暂无异常订单')" :image-size="56" />
      </section>

      <section class="operations-panel api-failures-panel">
        <div class="operations-panel-heading"><div><h2>{{ $t('近24小时接口失败明细') }}</h2><p>{{ $t('直接展示最近的上游失败原因，便于判断是否需要暂停客户下单') }}</p></div><button type="button" @click="gotoRoute('/transport/api-failure-logs', { hours: 24 })">{{ $t('查看全部') }}</button></div>
        <div v-if="adminOverview.apiFailures.length" class="api-failure-list">
          <div v-for="item in adminOverview.apiFailures" :key="item.id" class="api-failure-row">
            <span class="api-failure-action"><strong>{{ apiActionText(item.action) }}</strong><small>{{ item.customerName || $t('系统任务') }}</small></span>
            <span class="api-failure-reason">{{ item.failureReason }}</span>
            <el-tag size="small" type="danger">{{ item.resultCode || $t('失败') }}</el-tag>
            <time>{{ formatDate(item.createdAt) }}</time>
          </div>
        </div>
        <el-empty v-else :description="$t('近24小时没有接口失败')" :image-size="56" />
      </section>
    </div>
  </template>
</div>
</template>

<script>
import { getAccount, listOrders, listClaims, listDisputes, getOperationsOverview, updateOrderIntake } from '../../api/transport'
import { listPublishedNotices, listCustomerNotifications, markCustomerNotificationRead } from '../../api/system/notice'
import { mapGetters } from 'vuex'
import * as echarts from 'echarts'
import { getItem } from '../../utils/cookie'
import { TOKEN } from '../../constant/index'
import { LANGUAGE_EVENT, translateUi } from '../../i18n'
import LowBalanceNotice from './components/LowBalanceNotice.vue'

export default {
  name: 'Home',
  components: { LowBalanceNotice },

  data () {
    return {
      dashboardLoading: false,
      dashboardRequestId: 0,
      customerAccountLoading: false,
      customerAccount: {},
      balanceReminderAccountReady: false,
      balanceReminderNoticesReady: false,
      balanceReminderAccountSession: '',
      customerStats: {
        pendingBillingOrders: 0,
        pendingClaims: 0,
        pendingDisputes: 0,
        unreadMessages: 0,
        totalOrders: 0,
        successOrders: 0,
        pieceCount: 0,
        forecastWeightKg: 0,
        deductedAmount: 0,
        billedAmount: 0
      },
      dashboardNotices: [],
      dashboardMessages: [],
      customerNoticeDialog: false,
      selectedCustomerNotice: null,
      recentOrders: [],
      orderTrend: { dates: [], seriesByMetric: { count: [], weight: [], amount: [] } },
      orderTrendMetric: 'count',
      orderChart: null,
      adminOverviewLoading: false,
      adminControlSaving: false,
      adminOverview: { summary: {}, issues: [], exceptionOrders: [], apiFailures: [], orderIntake: {}, generatedAt: '' }
    }
  },

  computed: {
    ...mapGetters(['roles']),
    isCustomer () { return this.roles !== 'ROLE_ADMIN' },
    balanceReminderSessionKey () {
      if (this.roles !== 'ROLE_DEFAULT') return ''
      const user = this.$store.state && this.$store.state.user
      const token = (user && user.token) || getItem(TOKEN)
      const username = this.$store.getters.username
      return token && username ? `${username}:${token}` : ''
    },
    adminMetricCards () {
      const summary = this.adminOverview.summary || {}
      const today = String(this.adminOverview.generatedAt || '').slice(0, 10)
      const successfulToday = { status: 'SUCCESS', ...(/^\d{4}-\d{2}-\d{2}$/.test(today) ? { startTime: today, endTime: today } : {}) }
      return [
        { key: 'todayOrders', label: translateUi('今日新建订单'), value: summary.todayOrders, tip: translateUi('所有客户订单'), path: '/transport/tr-orders' },
        { key: 'todaySuccessfulOrders', label: translateUi('今日预报成功'), value: summary.todaySuccessfulOrders, tip: translateUi('上游已确认'), path: '/transport/tr-orders', query: { status: 'SUCCESS' } },
        { key: 'todaySalesAmount', label: translateUi('今日成功订单金额'), value: summary.todaySalesAmount, tip: translateUi('USD 预报成功订单冻结金额'), money: true, path: '/transport/tr-orders', query: successfulToday },
        { key: 'reviewOrders', label: translateUi('待核对订单'), value: summary.reviewOrders, tip: translateUi('需要人工确认'), path: '/transport/tr-orders', query: { status: 'REVIEW_REQUIRED' } },
        { key: 'recentApiFailures', label: translateUi('近24小时接口失败'), value: summary.recentApiFailures, tip: translateUi('上游调用失败'), path: '/transport/api-failure-logs', query: { hours: 24 } },
        { key: 'pendingDisputes', label: translateUi('待处理争议'), value: summary.pendingDisputes, tip: translateUi('客户争议'), path: '/transport/tr-disputes' },
        { key: 'insufficientDepositCustomers', label: translateUi('押金不足客户'), value: summary.insufficientDepositCustomers, tip: translateUi('低于目标押金'), path: '/transport/fin-deposits' },
        { key: 'pendingBillingOrders', label: translateUi('待出账订单'), value: summary.pendingBillingOrders, tip: translateUi('仍有未出账件数'), path: '/transport/tr-orders' }
      ]
    },
    latestCustomerNotice () { return this.dashboardNotices[0] || null },
    customerNoticeDocument () {
      const content = this.selectedCustomerNotice ? this.selectedCustomerNotice.content || '' : ''
      return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;padding:0 22px 24px;color:#303744;font:14px/1.7 Arial,sans-serif;overflow-wrap:anywhere;box-sizing:border-box}img{max-width:100%;height:auto}table{max-width:100%}</style></head><body>${content}</body></html>`
    },
    avatar () { return this.$store.getters.avatar },
    loginTime () { return this.$store.getters.loginTime },
    nickname () { return this.$store.getters.nickname },
    ipAddress () { return this.$store.getters.ipAddress },
    ipLocation () { return this.$store.getters.ipLocation }
  },

  created () {
    if (this.isCustomer) this.loadCustomerDashboard()
    else this.loadAdminOverview()
  },

  mounted () {
    if (this.isCustomer) this.$nextTick(() => this.renderOrderChart())
    window.addEventListener('resize', this.resizeOrderChart)
    // 趋势图绘制在 canvas 中，切换语言后需主动重绘才能生效
    window.addEventListener(LANGUAGE_EVENT, this.renderOrderChart)
  },

  deactivated () {
    // Cached routes detach their DOM. Do not reuse a detached notice frame:
    // its document can be blank when the customer returns to this page.
    this.customerNoticeDialog = false
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.resizeOrderChart)
    window.removeEventListener(LANGUAGE_EVENT, this.renderOrderChart)
    if (this.orderChart) {
      this.orderChart.dispose()
      this.orderChart = null
    }
  },

  methods: {
    formatMoney (value) {
      const amount = Number(value || 0)
      return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
    },
    formatDate (value) {
      return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
    },
    orderStatusText (status) {
      return { PREPARING: translateUi('待确认'), SUBMITTING: translateUi('处理中'), SUCCESS: translateUi('预报成功'), API_FAILED: translateUi('预报失败'), REVIEW_REQUIRED: translateUi('待核对') }[status] || status || '-'
    },
    apiActionText (action) {
      return { QUOTE: translateUi('运费试算'), QUOTE_BEFORE_ORDER: translateUi('下单前复核'), CREATE_ORDER: translateUi('创建订单'), ORDER_STATUS: translateUi('订单状态'), ORDER_DETAIL: translateUi('订单详情'), ORDER_DETAIL_UPDATE: translateUi('订单详情更新'), GET_LABEL: translateUi('获取面单'), ORDER_TRACE: translateUi('运单轨迹'), ORDER_WAYBILL: translateUi('查询运单号') }[action] || action || '上游接口'
    },
    dateRange () {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29)
      const format = value => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
      return { start: format(start), end: format(today) }
    },
    safeRequest (promise) {
      return promise.catch(() => ({}))
    },
    renderOrderChart () {
      if (!this.$refs.orderTrendChart) return
      if (!this.orderChart) this.orderChart = echarts.init(this.$refs.orderTrendChart)
      const metricSeries = this.orderTrend.seriesByMetric[this.orderTrendMetric] || []
      const fallbackName = this.$locale === 'en'
        ? { count: 'Order Count', weight: 'Weight', amount: 'Pending Charge' }[this.orderTrendMetric]
        : { count: '订单数', weight: '重量', amount: '预扣费' }[this.orderTrendMetric]
      this.orderChart.setOption({
        color: ['#6686d8', '#8fc66d', '#efbe54', '#7d6ac7', '#e58d70', '#55a7a0'],
        tooltip: { trigger: 'axis' },
        legend: { top: 0, right: 0, textStyle: { color: '#71807c', fontSize: 12 } },
        grid: { top: 38, left: 42, right: 18, bottom: 28 },
        xAxis: { type: 'category', boundaryGap: false, data: this.orderTrend.dates, axisLine: { lineStyle: { color: '#dfe8e4' } }, axisLabel: { color: '#84918d', fontSize: 11, interval: 4 } },
        yAxis: { type: 'value', minInterval: this.orderTrendMetric === 'count' ? 1 : 0.01, splitLine: { lineStyle: { color: '#edf2f0' } }, axisLabel: { color: '#84918d', fontSize: 11, formatter: value => this.orderTrendMetric === 'amount' ? Number(value).toFixed(2) : value } },
        series: (metricSeries.length ? metricSeries : [{ name: fallbackName, data: this.orderTrend.dates.map(() => 0) }]).map(item => ({ name: item.name, type: 'line', smooth: true, symbol: 'circle', symbolSize: 5, data: item.data }))
      })
    },
    resizeOrderChart () {
      if (this.orderChart) this.orderChart.resize()
    },
    buildOrderTrend (orders) {
      const range = this.dateRange()
      const dates = []
      const total = {}
      const success = {}
      const startDate = new Date(`${range.start}T00:00:00`)
      const dateKeys = []
      const products = {}
      const keyOf = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      for (let day = 0; day < 30; day += 1) {
        const cursor = new Date(startDate)
        cursor.setDate(startDate.getDate() + day)
        const key = keyOf(cursor)
        dateKeys.push(key)
        dates.push(key.slice(5))
        total[key] = 0
        success[key] = 0
      }
      ;(orders || []).forEach(order => {
        const raw = order.createdAt || order.createTime
        if (!raw) return
        const key = String(raw).replace('T', ' ').slice(0, 10)
        if (Object.prototype.hasOwnProperty.call(total, key)) {
          total[key] += 1
          if (order.status === 'SUCCESS') success[key] += 1
          const productName = order.productName || order.productCode || '其他产品'
          if (!products[productName]) products[productName] = { count: {}, weight: {}, amount: {} }
          products[productName].count[key] = Number(products[productName].count[key] || 0) + 1
          products[productName].weight[key] = Number(products[productName].weight[key] || 0) + Number(order.chargeWeight || 0)
          products[productName].amount[key] = Number(products[productName].amount[key] || 0) + Number(order.customerAmount || 0)
        }
      })
      const seriesByMetric = { count: [], weight: [], amount: [] }
      Object.keys(products).forEach(name => {
        ;['count', 'weight', 'amount'].forEach(metric => {
          seriesByMetric[metric].push({ name, data: dateKeys.map(key => Number(products[name][metric][key] || 0)) })
        })
      })
      this.orderTrend = { dates, seriesByMetric }
      this.$nextTick(() => this.renderOrderChart())
    },
    loadCustomerDashboard () {
      if (!this.isCustomer) return
      this.dashboardLoading = true
      this.customerAccountLoading = true
      this.balanceReminderAccountReady = false
      this.balanceReminderNoticesReady = false
      const reminderSession = this.balanceReminderSessionKey
      this.dashboardRequestId = Number(this.dashboardRequestId || 0) + 1
      const requestId = this.dashboardRequestId
      const range = this.dateRange()
      const orderParams = { pageNum: 1, pageSize: 5, startTime: range.start, endTime: range.end }
      const trendParams = { pageNum: 1, pageSize: 1000, startTime: range.start, endTime: range.end }
      let pendingRequests = 0
      const requestSection = (promise, onSuccess, onFinally) => {
        pendingRequests += 1
        this.safeRequest(promise).then(data => {
          if (requestId === this.dashboardRequestId) onSuccess(data || {})
        }).finally(() => {
          if (requestId !== this.dashboardRequestId) return
          if (onFinally) onFinally()
          pendingRequests -= 1
          if (pendingRequests === 0) this.dashboardLoading = false
        })
      }

      // Each section is requested at the same time and rendered as soon as
      // its own response arrives. A slow trend query can no longer hold back
      // the balance, notice, message, or order cards.
      requestSection(getAccount(), account => {
        this.customerAccount = account
        this.balanceReminderAccountSession = reminderSession
      }, () => { this.customerAccountLoading = false; this.balanceReminderAccountReady = true })
      requestSection(listOrders(false, orderParams), orders => {
        const summary = orders.summary || {}
        this.recentOrders = orders.list || []
        this.customerStats = {
          ...this.customerStats,
          totalOrders: Number(orders.total || 0),
          pieceCount: Number(summary.pieceCount || 0),
          forecastWeightKg: Number(summary.forecastWeightKg || 0).toFixed(3),
          deductedAmount: Number(summary.deductedAmount || 0),
          billedAmount: Number(summary.billedAmount || 0)
        }
      })
      requestSection(listOrders(false, trendParams), trendOrders => {
        this.buildOrderTrend(trendOrders.list || trendOrders.rows || [])
      })
      requestSection(listOrders(false, { pageNum: 1, pageSize: 1, billingStatus: 'OUTSTANDING' }), result => {
        this.customerStats = { ...this.customerStats, pendingBillingOrders: Number(result.total || 0) }
      })
      ;['SUCCESS'].forEach(status => {
        requestSection(listOrders(false, { ...orderParams, pageSize: 1, status }), result => {
          const field = { SUCCESS: 'successOrders' }[status]
          this.customerStats = { ...this.customerStats, [field]: Number(result.total || 0) }
        })
      })
      requestSection(listClaims(false, { pageNum: 1, pageSize: 1, status: 'OPEN' }), claims => {
        this.customerStats = { ...this.customerStats, pendingClaims: Number(claims.total || 0) }
      })
      requestSection(listDisputes(false, { pageNum: 1, pageSize: 1, closed: 'false' }), disputes => {
        this.customerStats = { ...this.customerStats, pendingDisputes: Number(disputes.total || 0) }
      })
      requestSection(listPublishedNotices({ pageNum: 1, pageSize: 1000 }), notices => {
        this.dashboardNotices = notices.list || []
        const latestNotice = this.dashboardNotices[0]
        if (latestNotice && this.shouldAutoOpenCustomerNotice(latestNotice)) {
          this.selectedCustomerNotice = latestNotice
          this.customerNoticeDialog = true
        }
      }, () => { this.balanceReminderNoticesReady = !this.customerNoticeDialog })
      requestSection(listCustomerNotifications({ pageNum: 1, pageSize: 6 }), notifications => {
        this.customerStats = { ...this.customerStats, unreadMessages: Number(notifications.unread || 0) }
        this.dashboardMessages = (notifications.list || []).filter(item => item.notificationType !== 'SYSTEM_NOTICE').slice(0, 6)
      })
    },
    goCustomerRoute (path, query) {
      this.$router.push({ path, query: query || {} })
    },
    shouldAutoOpenCustomerNotice (notice) {
      try {
        // Use the current access token to distinguish login sessions. The
        // server login time can be unchanged while a browser keeps the token,
        // and an old sessionStorage key must not suppress a new login notice.
        const userKey = this.username || this.$store.getters.userId || 'current-user'
        const dismissedKey = `transport-customer-notice-dismissed:${userKey}:${notice.id}`
        if (localStorage.getItem(dismissedKey)) return false
        const loginKey = getItem(TOKEN) || this.loginTime || 'current-session'
        const key = `transport-customer-notice:${loginKey}:${notice.id}`
        if (sessionStorage.getItem(key)) return false
        sessionStorage.setItem(key, '1')
      } catch (error) {
        // Private browsing can disable sessionStorage; showing the notice is
        // still preferable to silently hiding it.
      }
      return true
    },
    dismissCustomerNotice () {
      const notice = this.selectedCustomerNotice
      if (!notice) return
      try {
        const userKey = this.username || this.$store.getters.userId || 'current-user'
        localStorage.setItem(`transport-customer-notice-dismissed:${userKey}:${notice.id}`, '1')
      } catch (error) {
        // Storage can be unavailable in private browsing; the current dialog can still be closed.
      }
      this.customerNoticeDialog = false
    },
    openCustomerNotice (notice) {
      this.selectedCustomerNotice = notice
      this.customerNoticeDialog = true
    },
    openCustomerNotices () {
      this.selectedCustomerNotice = this.dashboardNotices[0] || null
      this.customerNoticeDialog = true
    },
    openCustomerMessage (message) {
      const show = () => this.$alert(message.content || this.$t('暂无消息内容'), message.title || this.$t('消息'), { confirmButtonText: this.$t('知道了'), customClass: 'customer-message-original' })
      if (!message.unread) return show()
      markCustomerNotificationRead(message.id).then(() => { message.unread = 0; this.customerStats.unreadMessages = Math.max(0, this.customerStats.unreadMessages - 1); show() }).catch(show)
    },
    loadAdminOverview () {
      if (this.isCustomer) return
      this.adminOverviewLoading = true
      getOperationsOverview().then(data => {
        this.adminOverview = {
          summary: data.summary || {},
          issues: data.issues || [],
          exceptionOrders: data.exceptionOrders || [],
          apiFailures: data.apiFailures || [],
          orderIntake: data.orderIntake || {},
          generatedAt: data.generatedAt || ''
        }
      }).catch(error => this.$message.error(translateUi((error && error.message) || translateUi('运行总览读取失败'))))
        .finally(() => { this.adminOverviewLoading = false })
    },
    pauseOrderIntake () {
      this.$prompt(translateUi('请填写暂停原因，客户试算和下单时会看到此说明。'), translateUi('紧急暂停客户下单'), {
        confirmButtonText: translateUi('确认暂停'), cancelButtonText: translateUi('取消'), inputType: 'textarea', inputPattern: /\S+/, inputErrorMessage: translateUi('必须填写暂停原因'), inputPlaceholder: translateUi('例如：上游订单接口异常，正在紧急排查')
      }).then(({ value }) => this.saveOrderIntake(true, value)).catch(() => {})
    },
    resumeOrderIntake () {
      this.$confirm(translateUi('恢复后客户可以立即重新试算和创建订单，确定继续吗？'), translateUi('恢复客户下单'), { confirmButtonText: translateUi('确认恢复'), cancelButtonText: translateUi('取消'), type: 'warning' })
        .then(() => this.saveOrderIntake(false, '')).catch(() => {})
    },
    saveOrderIntake (paused, reason) {
      this.adminControlSaving = true
      return updateOrderIntake({ paused, reason }).then(control => {
        this.adminOverview = { ...this.adminOverview, orderIntake: control }
        this.$message.success(translateUi(paused ? translateUi('客户新订单入口已暂停') : translateUi('客户新订单入口已恢复')))
        return this.loadAdminOverview()
      }).catch(error => this.$message.error(translateUi((error && error.message) || translateUi('运行控制保存失败'))))
        .finally(() => { this.adminControlSaving = false })
    },
    openOperationIssue (issue) {
      if (issue.path) this.gotoRoute(issue.path, issue.query)
    },
    gotoRoute (path, query) {
      this.$router.push(query ? { path, query } : path)
    }
  }
}
</script>

<style lang="scss" scoped>
.home-container {
  padding: 0 20px;

  .customer-dashboard-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 20px 0 16px;

    h1 { margin: 0; color: #243735; font-size: 24px; font-weight: 700; }
    p { margin: 7px 0 0; color: #7b8986; font-size: 13px; }
  }

  .dashboard-row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    margin-bottom: 16px;

    // Element UI columns normally keep their own height. Make each column a
    // flex item so the panels in the same row share the tallest panel height.
    > [class*='el-col-'] {
      display: flex;
      flex-direction: column;
    }

    .dashboard-panel { width: 100%; }
  }

  .dashboard-panel {
    height: 100%;
    box-sizing: border-box;
    padding: 18px;
    border: 1px solid #e1e9e6;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 3px 12px rgba(31, 76, 69, .04);
  }

  .panel-heading {
    display: flex;
    align-items: baseline;
    gap: 9px;
    margin-bottom: 14px;

    h2 { margin: 0; color: #243735; font-size: 17px; font-weight: 700; }
    span { color: #8a9995; font-size: 12px; }
    button { margin-left: auto; padding: 0; border: 0; color: #2c8c7e; font-size: 12px; background: transparent; cursor: pointer; }
  }

  .todo-item, .notice-row, .message-row, .recent-order-row, .stat-item, .quick-action, .help-item {
    border: 0;
    background: transparent;
    cursor: pointer;
  }

  .todo-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 42px;
    margin-top: 8px;
    padding: 0 13px;
    color: #445653;
    text-align: left;
    background: #f1f6f4;
    transition: background .15s ease;

    &:hover { background: #e5f1ed; }
    strong { min-width: 26px; color: #268878; font-size: 16px; text-align: right; }
  }

  .quick-action-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .quick-action { display: flex; align-items: center; justify-content: center; gap: 8px; min-height: 60px; border: 1px solid #e2ece8; color: #36514d; font-size: 13px; }
  .quick-action:hover { border-color: #69ae9f; color: #1f8576; background: #f4fbf8; }
  .quick-action i { color: #319382; font-size: 20px; }

  .notice-panel, .message-panel { min-height: 224px; }
  .message-list { max-height: 175px; overflow-y: auto; }
  .dashboard-notice-list { max-height: 240px; overflow-y: auto; border-top: 1px solid #edf2f0; }
  .notice-row { display: flex; align-items: center; width: 100%; min-height: 44px; gap: 16px; padding: 0 12px; border-bottom: 1px solid #edf2f0; color: #43534f; text-align: left; transition: color .15s ease, background .15s ease; }
  .notice-row:hover { color: #218777; background: #f1f7f5; }
  .notice-row-title { min-width: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .notice-row time { flex: none; color: #8f9c98; font-size: 11px; }
  .notice-empty { display: flex; flex: 1; align-items: center; justify-content: center; min-height: 240px; margin: 8px; gap: 8px; color: #a1afaa; font-size: 13px; }
  .notice-empty i { color: #6ba99c; font-size: 20px; }
  .message-row { display: flex; align-items: center; width: 100%; gap: 10px; padding: 11px 0; border: 0; border-bottom: 1px solid #edf2f0; background: transparent; color: #43534f; cursor: pointer; text-align: left; }
  .message-row:hover { color: #218777; }
  .message-row span { min-width: 0; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .message-row time { flex: none; color: #9aa6a2; font-size: 11px; }
  .message-row i { flex: none; color: #3b9889; font-size: 16px; }
  .customer-notice-dialog-body { min-height: 480px; padding: 28px 34px 32px; border-top: 1px solid #edf2f0; box-sizing: border-box; }
  .customer-notice-dialog-content { min-width: 0; padding: 0 4px; }
  .customer-notice-dialog-kicker { display: flex; align-items: center; gap: 7px; color: #2c8c7e; font-size: 12px; }
  .customer-notice-dialog-content h3 { margin: 14px 0 0; color: #243735; font-size: 21px; line-height: 1.45; overflow-wrap: anywhere; }
  .customer-notice-dialog-time { margin: 8px 0 22px; color: #9aa6a2; font-size: 12px; }
  .customer-notice-dialog-content iframe { display: block; width: 100%; height: 360px; border: 1px solid #edf2f0; border-radius: 8px; background: #fff; }
  .customer-notice-dialog-footer { display: flex; justify-content: flex-end; align-items: center; gap: 8px; }

  .admin-notice-content { padding: 14px 20px 24px; color: #303744; }
  .admin-notice-kicker { display: flex; align-items: center; gap: 8px; color: #2c8c7e; font-size: 13px; }
  .admin-notice-kicker i { font-size: 18px; }
  .admin-notice-content h2 { margin: 18px 0 0; color: #243735; font-size: 24px; line-height: 1.45; overflow-wrap: anywhere; }
  .admin-notice-time { margin: 8px 0 22px; color: #9aa6a2; font-size: 12px; }
  .admin-notice-content .notice { max-height: 480px; padding: 0 8px 16px; overflow-y: auto; line-height: 1.8; }

  ::v-deep .customer-notice-dialog .el-dialog__body,
  ::v-deep .admin-notice-dialog .el-dialog__body { padding: 0; }

  .balance-panel { margin-bottom: 16px; }
  .balance-content { display: flex; align-items: stretch; min-height: 74px; }
  .balance-main { display: flex; flex: 1.2; align-items: baseline; gap: 8px; padding: 6px 22px 6px 0; border-right: 1px solid #edf2f0; }
  .balance-main span, .balance-item span { display: block; color: #788985; font-size: 12px; }
  .balance-main strong { color: #218777; font-size: 30px; font-weight: 700; }
  .balance-main em { color: #80908b; font-size: 12px; font-style: normal; }
  .balance-item { flex: 1; padding: 6px 22px; border-right: 1px solid #edf2f0; }
  .balance-item:last-child { border-right: 0; }
  .balance-item strong { display: block; margin-top: 14px; color: #334944; font-size: 16px; font-weight: 600; }

  .statistics-panel { min-height: 300px; }
  .statistics-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
  .trend-toolbar { display: flex; justify-content: flex-end; min-height: 28px; margin-top: 12px; }
  .trend-toolbar ::v-deep .el-radio { margin-right: 16px; color: #71807c; font-size: 12px; }
  .trend-toolbar ::v-deep .el-radio__label { padding-left: 5px; }
  .order-trend-chart { width: 100%; height: 250px; margin-top: 18px; }
  .stat-item { padding: 13px; border: 1px solid #e6eeeb; text-align: left; }
  .stat-item:hover { border-color: #69ae9f; background: #f7fcfa; }
  .stat-item span { display: block; color: #788985; font-size: 12px; }
  .stat-item strong { display: block; margin-top: 8px; color: #2f4f49; font-size: 20px; font-weight: 700; }
  .recent-orders { margin-top: 18px; }
  .subheading { margin-bottom: 5px; color: #667a75; font-size: 12px; font-weight: 600; }
  .recent-order-row { display: grid; grid-template-columns: 1.2fr 1fr .7fr .8fr; gap: 8px; width: 100%; padding: 8px 0; border-bottom: 1px solid #edf2f0; color: #4d5e5a; text-align: left; font-size: 12px; }
  .recent-order-row:hover { color: #218777; }
  .recent-order-row span, .recent-order-row strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .recent-order-row strong { color: #2f4f49; text-align: right; }

  @media (max-width: 900px) {
    .balance-content { flex-wrap: wrap; }
    .balance-main { flex-basis: 100%; border-right: 0; border-bottom: 1px solid #edf2f0; }
    .balance-item { padding: 14px 12px 6px; }
  }

  @media (max-width: 600px) {
    padding: 0 12px;
    .customer-dashboard-heading { align-items: flex-start; gap: 10px; }
    .customer-dashboard-heading h1 { font-size: 20px; }
    .statistics-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .order-trend-chart { height: 220px; }
    .customer-notice-dialog-body { min-height: 360px; padding: 20px 18px 24px; }
    .customer-notice-dialog-content { padding: 0 2px; }
    .customer-notice-dialog-content h3 { font-size: 18px; }
    .customer-notice-dialog-content iframe { height: 260px; }
    .notice-row { min-height: 42px; gap: 10px; padding: 0 8px; }
    .notice-row time { font-size: 10px; }
    .recent-order-row { grid-template-columns: 1fr .8fr; }
    .recent-order-row span:nth-child(2) { display: none; }
  }

  .operations-dashboard { padding: 20px 0 28px; }
  .operations-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
  .operations-heading h1 { margin: 0; color: #243735; font-size: 25px; }
  .operations-heading p { margin: 7px 0 0; color: #7d8c88; font-size: 13px; }

  .order-intake-control { display: flex; align-items: center; gap: 16px; padding: 18px 20px; border: 1px solid #cfe7df; border-radius: 8px; background: linear-gradient(135deg, #f3fbf8, #fff); box-shadow: 0 4px 16px rgba(30, 92, 80, .05); }
  .order-intake-control.paused { border-color: #f4b9b3; background: linear-gradient(135deg, #fff4f2, #fff); }
  .control-icon { display: flex; flex: none; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 50%; color: #218777; background: #dff2ec; font-size: 23px; }
  .order-intake-control.paused .control-icon { color: #d75548; background: #fde1dd; }
  .control-copy { min-width: 0; flex: 1; }
  .control-title { display: flex; align-items: center; gap: 10px; color: #263b37; font-size: 17px; font-weight: 700; }
  .control-copy p { margin: 7px 0 0; color: #73827e; font-size: 12px; line-height: 1.6; }

  .operations-metrics { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 14px; margin: 16px 0; }
  .operation-metric { min-width: 0; padding: 18px; border: 1px solid #e1e9e6; border-radius: 7px; text-align: left; background: #fff; box-shadow: 0 3px 12px rgba(31, 76, 69, .04); cursor: pointer; transition: border-color .15s ease, transform .15s ease, box-shadow .15s ease; }
  .operation-metric:hover { border-color: #74b5a7; transform: translateY(-1px); box-shadow: 0 7px 18px rgba(31, 76, 69, .08); }
  .operation-metric span { display: block; color: #697b76; font-size: 13px; }
  .operation-metric strong { display: block; margin-top: 10px; overflow: hidden; color: #213a35; font-size: 27px; line-height: 1.15; text-overflow: ellipsis; white-space: nowrap; }
  .operation-metric small { display: block; margin-top: 8px; color: #9aa6a2; font-size: 11px; }

  .operations-content-grid { display: grid; grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr); gap: 16px; margin-bottom: 16px; }
  .operations-panel { box-sizing: border-box; padding: 18px 20px; border: 1px solid #e1e9e6; border-radius: 7px; background: #fff; box-shadow: 0 3px 12px rgba(31, 76, 69, .04); }
  .operations-panel-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 13px; }
  .operations-panel-heading h2 { margin: 0; color: #263b37; font-size: 17px; }
  .operations-panel-heading p { margin: 5px 0 0; color: #96a29f; font-size: 11px; }
  .operations-panel-heading > span { flex: none; color: #7d8c88; font-size: 12px; }
  .operations-panel-heading > button { border: 0; color: #268878; background: transparent; cursor: pointer; }

  .operation-issue { display: flex; align-items: center; width: 100%; min-height: 59px; gap: 11px; padding: 9px 2px; border: 0; border-bottom: 1px solid #edf2f0; text-align: left; background: transparent; cursor: pointer; }
  .operation-issue:last-child { border-bottom: 0; }
  .operation-issue:disabled { cursor: default; }
  .operation-issue:not(:disabled):hover .issue-copy strong { color: #218777; }
  .issue-indicator { width: 8px; height: 8px; flex: none; border-radius: 50%; background: #8496a3; box-shadow: 0 0 0 4px rgba(132, 150, 163, .12); }
  .issue-indicator.is-danger { background: #e35b50; box-shadow: 0 0 0 4px rgba(227, 91, 80, .12); }
  .issue-indicator.is-warning { background: #e79a3b; box-shadow: 0 0 0 4px rgba(231, 154, 59, .12); }
  .issue-copy { min-width: 0; flex: 1; }
  .issue-copy strong, .issue-copy small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .issue-copy strong { color: #354b46; font-size: 13px; }
  .issue-copy small { margin-top: 5px; color: #8c9995; font-size: 11px; }
  .operation-issue > i { flex: none; color: #a0aca8; }
  .operation-healthy { display: flex; min-height: 210px; flex-direction: column; align-items: center; justify-content: center; color: #83938e; }
  .operation-healthy i { margin-bottom: 12px; color: #39a28e; font-size: 34px; }
  .operation-healthy strong { color: #35514b; font-size: 14px; }
  .operation-healthy span { margin-top: 7px; font-size: 11px; }

  .operation-quick-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .operation-quick-grid button { display: flex; min-height: 76px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; border: 1px solid #e3ece9; border-radius: 6px; color: #48625c; background: #fbfdfc; cursor: pointer; }
  .operation-quick-grid button:hover { border-color: #74b5a7; color: #218777; background: #f2faf7; }
  .operation-quick-grid i { color: #2d9382; font-size: 21px; }
  .operation-quick-grid span { font-size: 12px; }

  .exception-order { display: grid; grid-template-columns: minmax(190px, 1.1fr) minmax(220px, 1.6fr) 90px 130px 16px; align-items: center; width: 100%; min-height: 61px; gap: 12px; padding: 8px 2px; border: 0; border-bottom: 1px solid #edf2f0; text-align: left; background: transparent; cursor: pointer; }
  .exception-order:last-child { border-bottom: 0; }
  .exception-order:hover { background: #f8fbfa; }
  .exception-main { min-width: 0; }
  .exception-main strong, .exception-main small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .exception-main strong { color: #314a45; font-size: 13px; }
  .exception-main small { margin-top: 5px; color: #8c9995; font-size: 11px; }
  .exception-reason { overflow: hidden; color: #647570; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
  .exception-order time { color: #8c9995; font-size: 11px; }
  .exception-order > i { color: #9aa8a4; }
  .api-failures-panel { margin-top: 16px; }
  .api-failure-row { display: grid; grid-template-columns: minmax(150px, .8fr) minmax(240px, 1.8fr) 90px 130px; align-items: center; min-height: 58px; gap: 12px; border-bottom: 1px solid #edf2f0; }
  .api-failure-row:last-child { border-bottom: 0; }
  .api-failure-action { min-width: 0; }
  .api-failure-action strong, .api-failure-action small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .api-failure-action strong { color: #344a45; font-size: 13px; }
  .api-failure-action small { margin-top: 5px; color: #8c9995; font-size: 11px; }
  .api-failure-reason { overflow: hidden; color: #657670; font-size: 12px; text-overflow: ellipsis; white-space: nowrap; }
  .api-failure-row time { color: #8c9995; font-size: 11px; }

  @media (max-width: 1100px) {
    .operations-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .operations-content-grid { grid-template-columns: 1fr; }
  }

  @media (max-width: 700px) {
    .operations-dashboard { padding-top: 14px; }
    .operations-heading { align-items: flex-start; }
    .operations-heading h1 { font-size: 21px; }
    .operations-heading p { max-width: 220px; line-height: 1.5; }
    .order-intake-control { align-items: flex-start; flex-wrap: wrap; padding: 15px; }
    .control-icon { width: 38px; height: 38px; font-size: 20px; }
    .control-copy { flex-basis: calc(100% - 56px); }
    .order-intake-control > .el-button { width: 100%; margin-left: 0; }
    .operations-metrics { gap: 10px; }
    .operation-metric { padding: 14px 13px; }
    .operation-metric strong { font-size: 22px; }
    .operations-panel { padding: 16px 14px; }
    .operation-issue { gap: 9px; }
    .issue-copy small { white-space: normal; line-height: 1.45; }
    .exception-order { grid-template-columns: minmax(0, 1fr) auto; gap: 6px 10px; padding: 12px 2px; }
    .exception-main { grid-column: 1; }
    .exception-reason { grid-column: 1 / -1; grid-row: 2; white-space: normal; line-height: 1.45; }
    .exception-order .el-tag { grid-column: 2; grid-row: 1; }
    .exception-order time { grid-column: 1; grid-row: 3; }
    .exception-order > i { grid-column: 2; grid-row: 3; justify-self: end; }
    .api-failure-row { grid-template-columns: minmax(0, 1fr) auto; gap: 6px 10px; padding: 11px 2px; }
    .api-failure-action { grid-column: 1; grid-row: 1; }
    .api-failure-row .el-tag { grid-column: 2; grid-row: 1; }
    .api-failure-reason { grid-column: 1 / -1; grid-row: 2; white-space: normal; line-height: 1.45; }
    .api-failure-row time { grid-column: 1; grid-row: 3; }
  }

  .el-card {
    margin-top: 24px;
    box-shadow: 0 0.5px 1px rgba(0, 0, 0, 0.12);
  }

  .welcome-content {
    display: flex;
    align-items: center;
    min-height: 64px;
  }

  .avatar-image {
    flex: 0 0 auto;
  }

  .welcome-text {
    flex: 1;
    margin-left: 16px;
  }

  .welcome-title {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }

  .welcome-meta {
    margin-top: 8px;
    color: #909399;
    font-size: 13px;
  }

  .card-title {
    color: #303133;
    font-weight: 600;
  }

  .capability-list,
  .quick-list {
    margin-top: 18px;
  }

  .capability-item {
    display: flex;
    align-items: center;
    min-height: 74px;
    padding: 14px;
    border: 1px solid #ebeef5;
    margin-bottom: 12px;

    .svg-icon {
      flex: 0 0 auto;
      margin-right: 12px;
      font-size: 28px;
      color: #409eff;
    }
  }

  .capability-title {
    color: #303133;
    font-size: 14px;
    font-weight: 600;
  }

  .capability-desc {
    margin-top: 6px;
    color: #909399;
    font-size: 12px;
  }

  .quick-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 88px;
    border: 1px solid #ebeef5;
    cursor: pointer;

    &:hover {
      box-shadow: 0 1px 5px rgba(0, 0, 0, 0.14);
    }

    .svg-icon {
      font-size: 27px;
      color: #409eff;
    }

    span {
      margin-top: 8px;
      color: #303133;
      font-size: 13px;
    }
  }

  .notice img {
    max-width: 100%;
  }
}
</style>
